import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, gr, eur, reportUrl } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { WarningCircle, ClockCountdown, ArrowsClockwise, Receipt } from "@phosphor-icons/react";

export default function Alerts() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["alerts"],
    queryFn: async () => (await api.get("/alerts")).data,
    staleTime: 0,
    refetchOnMount: "always",
    refetchInterval: 10000,
  });

  if (isLoading || !data) return <div className="p-6 text-muted-foreground">Φόρτωση...</div>;

  const issueInvoice = async (b) => {
    try {
      const { data } = await api.post("/fiscal/issue", {
        boat_id: b.id,
        doc_type: "invoice",
        amount: Number(b.suggested_amount || 0),
        period_end: b.end_date || null,
      });
      window.open(reportUrl(`/fiscal/${data.id}.pdf`), "_blank");
      await qc.invalidateQueries({ queryKey: ["alerts"] });
      await qc.invalidateQueries({ queryKey: ["fiscal"] });
      if (data.mydata_status === "success") toast.success(`Τιμολόγιο ${data.series}/${data.aa} · ΜΑΡΚ ${data.mydata_mark}`);
      else if (data.mydata_status === "error") toast.warning(`Εκδόθηκε ${data.series}/${data.aa}, αλλά η αποστολή στο myDATA απέτυχε: ${data.mydata_error || ""}`);
      else toast.success(`Εκδόθηκε τιμολόγιο ${data.series}/${data.aa}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα έκδοσης τιμολογίου");
    }
  };

  const approveRenewal = async (id) => {    try {
      await api.post(`/contracts/${id}/renew`);
      await qc.invalidateQueries();
      toast.success("Η σύμβαση ανανεώθηκε κατά ένα έτος");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα ανανέωσης");
    }
  };

  const Section = ({ title, icon: Icon, tone, items, empty, render }) => (
    <Card className="rounded-sm border-border shadow-none" data-testid={`alerts-${title}`}>
      <div className="flex items-center gap-2 border-b border-border px-5 py-4">
        <Icon size={18} weight="bold" className={tone} />
        <h3 className="font-head font-bold">{title}</h3>
        <span className="ml-auto rounded-sm bg-secondary px-2 py-0.5 text-xs font-semibold tabular-nums">{items.length}</span>
      </div>
      <div className="divide-y divide-border">
        {items.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-muted-foreground">{empty}</div>
        ) : (
          items.map(render)
        )}
      </div>
    </Card>
  );

  const expiryItems = [...data.expired, ...data.expiring];

  return (
    <div>
      <PageHeader title="Ειδοποιήσεις" subtitle="Συμβάσεις που λήγουν στις επόμενες 7 ημέρες ή έχουν λήξει" />
      <div className="grid gap-6 p-6">
        <Section
          title="Λήξη σύμβασης εντός 7 ημερών"
          icon={ClockCountdown}
          tone="text-[#F59E0B]"
          items={expiryItems}
          empty="Καμία σύμβαση δεν λήγει στις επόμενες 7 ημέρες."
          render={(b) => (
            <div key={b.id} className="flex items-center justify-between px-5 py-3 text-sm" data-testid={`expiry-${b.id}`}>
              <div>
                <div className="font-medium">{b.name} <span className="text-muted-foreground">· #{b.slot_number}</span></div>
                <div className="text-xs text-muted-foreground">{b.owner} · {b.days_left < 0 ? "έληξε" : "λήγει"} {gr(b.end_date)}</div>
              </div>
              {b.days_left < 0 ? (
                <span className="rounded-sm bg-destructive/10 px-2 py-0.5 text-xs font-semibold text-destructive tabular-nums">{Math.abs(b.days_left)} ημ. πριν</span>
              ) : (
                <span className="rounded-sm bg-[#F59E0B]/10 px-2 py-0.5 text-xs font-semibold text-[#B45309] tabular-nums">σε {b.days_left} ημ.</span>
              )}
            </div>
          )}
        />
        <Section
          title="Ολοκληρωμένη διάρκεια παραμονής — εκκρεμεί τιμολόγιο"
          icon={Receipt}
          tone="text-[#0F766E]"
          items={data.pending_invoices || []}
          empty="Δεν εκκρεμεί έκδοση τιμολογίου."
          render={(b) => (
            <div key={b.id} className="flex items-center justify-between px-5 py-3 text-sm" data-testid={`pending-invoice-${b.id}`}>
              <div>
                <div className="font-medium">{b.name} <span className="text-muted-foreground">· #{b.slot_number}</span></div>
                <div className="text-xs text-muted-foreground">{b.owner} · παραμονή έως {gr(b.end_date)} · ποσό {eur(b.suggested_amount)} (με ΦΠΑ)</div>
              </div>
              <Button size="sm" className="ml-3 rounded-sm bg-[#0F766E] hover:bg-[#0d5f59]" onClick={() => issueInvoice(b)} data-testid={`issue-invoice-${b.id}`}>
                <Receipt size={14} weight="bold" /> Έκδοση τιμολογίου
              </Button>
            </div>
          )}
        />
        <Section
          title="Αιτήματα ανανέωσης από πελάτες"
          icon={ArrowsClockwise}
          tone="text-[#0055FF]"
          items={data.renewal_requests || []}
          empty="Δεν υπάρχουν αιτήματα ανανέωσης."
          render={(b) => (
            <div key={b.id} className="flex items-center justify-between px-5 py-3 text-sm" data-testid={`renewal-req-${b.id}`}>
              <div>
                <div className="font-medium">{b.name} <span className="text-muted-foreground">· #{b.slot_number}</span></div>
                <div className="text-xs text-muted-foreground">{b.owner} · λήξη {gr(b.end_date)}</div>
              </div>
              <span className="rounded-sm bg-[#0055FF]/10 px-2 py-0.5 text-xs font-semibold text-[#0044CC]">Ζήτησε ανανέωση</span>
              <Button size="sm" className="ml-3 rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" onClick={() => approveRenewal(b.id)} data-testid={`approve-renewal-${b.id}`}>
                <ArrowsClockwise size={14} weight="bold" /> Ανανέωση +1 έτος
              </Button>
            </div>
          )}
        />
      </div>
    </div>
  );
}
